<template class="page-content">
  <ModalLayout ref="offerInfoModal" modal-id="offer-info-modal"
    :loading="isSavableStateLoading"
    @close="modalClosed">
    <template #modal-header>
      <h5 v-if="selectedMatch && selectedMatch.transplant_center" class="modal-title">
        {{ `Offer Info - Donor: ${selectedMatch.donor.code} | Match: ${selectedMatch.code}-${selectedMatch.organ.name} | TC: ${selectedMatch.transplant_center.name}` }}
      </h5>
    </template>
    <template #modal-body>
      <div v-if="!isSavableStateLoading && selectedMatch" class="offer-info">
        <div class="offer-info-response">
          <span class="offer-info-response-label">Stop Condition</span>
          <span class="badge badge-pill" :class="offerResponseClass">{{ offerResponse }}</span>
        </div>

        <div class="row">
          <div class="col-6">
            <div class="card offer-info-card">
              <div class="card-header">Page Visit History</div>
              <ul class="list-group list-group-flush">
                <li v-for="page in pageVisits" :key="page.label" class="list-group-item offer-info-visit-row">
                  <span class="offer-info-visit-label">
                    <font-awesome-icon
                      :class="page.visited ? 'text-success' : 'text-muted'"
                      :icon="['fas', page.visited ? 'fa-circle-check' : 'fa-circle-minus']" />
                    {{ page.label }}
                  </span>
                  <span :class="page.visited ? '' : 'text-muted'">
                    {{ page.visitedAt }}
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div class="col-6">
            <div class="card offer-info-card">
              <div class="card-header">Offer Details Visits</div>
              <table v-if="visitedOffers.length" class="table table-sm table-hover offer-info-table mb-0">
                <thead>
                  <tr>
                    <th>Seq</th>
                    <th>Date Time</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="offer in visitedOffers" :key="offer.seq">
                    <td>{{ offer.seq }}</td>
                    <td>{{ formatDateTimeET(offer.offer_details_page_visited_at) }} ET</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="offer-info-empty text-muted">Not visited</div>
            </div>
          </div>
        </div>
       
      </div>
    </template>
  </ModalLayout>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
// @ts-ignore
import ModalLayout from "@/src/components/shared/ModalLayout.vue";
// @ts-ignore
import { useMatchStore } from '@/src/stores/match';
// @ts-ignore
import { formatDateTimeET } from "@/src/date-helpers";
import { useSavableState } from '@/src/composables/useSavableState';
import { useModalActions } from '@/src/composables/useModalActions';
import { Match } from "@/src/models/match";

const props = defineProps({
  matchId: { required: true, type: String }
});

const emit = defineEmits(['close']);
const matchStore = useMatchStore();

const {
  isSavableStateLoading,
  setSavableStateLoading,
  setSavableStateIdle,
  setSavableStateBlank,
  handleFormError,
} = useSavableState();

const offerInfoModal = ref<InstanceType<typeof ModalLayout> | null>(null);

const selectedMatch = ref<Match | null>(null);

const offerResponse = computed(() => selectedMatch.value?.offerResponse ?? 'None');
const offerResponseClass = computed(() => selectedMatch.value?.offerResponseClass ?? 'badge-info');
const pageVisits = computed(() => selectedMatch.value?.pageVisits ?? []);
const visitedOffers = computed(() => selectedMatch.value?.visitedOffers ?? []);

async function getModalData() {
  if (!props.matchId) return;

  try {
    setSavableStateLoading();
    const response = await matchStore.loadMatch(props.matchId);
    const match = Match.buildFromAPI(response);
    selectedMatch.value = match;

    setSavableStateIdle();
  } catch (error) {
    handleFormError(error);
  }
}

watch(() => props.matchId, async () => {
  await getModalData();
});

onMounted(async () => {
  selectedMatch.value = null;
  await getModalData();
});

const { showModal, closeModal, closeAllModals, modalClosed } = useModalActions(offerInfoModal, emit, {
  onClose: setSavableStateBlank,
});

defineExpose({ showModal, closeModal, closeAllModals });
</script>

<style scoped>
.offer-info-response {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.offer-info-response-label {
  font-weight: 600;
}

.offer-info-card {
  margin-bottom: 1rem;
}

.offer-info-card:last-child {
  margin-bottom: 0;
}

.offer-info-visit-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.offer-info-visit-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.offer-info-table th,
.offer-info-table td {
  padding: 0.5rem 1rem;
}

.offer-info-empty {
  padding: 0.75rem 1rem;
}
.badge {
  position: inherit !important;
  padding: 0.25em 0.75em !important;
  border-radius: 10rem !important;
}
</style>
